import React, { useState, useEffect } from 'react';
import type { ComponentProps } from 'react';

import { getCookie, setCookie } from '@zapier/common-utils';

import type { UseQuery, UseMutation } from '../../../types';

import { PolicyModal } from '../PolicyModal';
import { usePolicyData } from './usePolicyData';

const SNOOZE_COOKIE_PREFIX = 'policySnooze';
const SNOOZE_COOKIE_EXP = 60 * 60;

const EVENT_POLICY_PROMPT_SHOWN = 'policy_shown';
const EVENT_POLICY_AGREED = 'policy_agreement';

const formatSlugForCookie = (slug: string) => `${SNOOZE_COOKIE_PREFIX}_${slug}`;

const silenceLogger = (slug: string, reasons: string): void => {
  console.info(`Silencing pending policy \`${slug}\` because ${reasons}.`);
};

type TrackingContext = {
  trackEvent: (eventName: string, metadata: {}) => void;
};

type Props = {
  /**
   * Current pathname of page to exempt policies that apply.
   */
  currentPathname: string;
  /**
   * Function to render markdown.
   */
  renderMarkdown?: ComponentProps<typeof PolicyModal>['renderMarkdown'];
  /**
   * `useQuery` hook to actually perform the network request.
   */
  useQuery: UseQuery;
  /**
   * `useMutation` hook to actually perform the network request.
   */
  useMutation: UseMutation;
  /**
   * `useTrackingContext` hook that returns functions to actually
   * perform the network request.
   */
  useTrackingContext: () => TrackingContext;
};

export const PolicyPendingCheck = (props: Props) => {
  const [, forceUpdate] = useState({});
  const { trackEvent } = props.useTrackingContext();
  const { policies: pendingPolicies } = usePolicyData({
    pending: true,
    first: 1,
    useQuery: props.useQuery,
    useMutation: props.useMutation,
    onMutationError: () => {
      const { slug } = pendingPolicies[0];
      setCookie(
        formatSlugForCookie(slug),
        'from_accept_err',
        SNOOZE_COOKIE_EXP
      );
      forceUpdate({});
    },
  });
  const policy = pendingPolicies[0];
  useEffect(() => {
    if (policy) {
      const eventParams = {
        policy_slug: policy.slug,
        policy_content: policy.contentId,
      };
      trackEvent(EVENT_POLICY_PROMPT_SHOWN, eventParams);
    }
  }, [policy, trackEvent]);

  if (!policy) {
    return null;
  }

  // Exempt the modal for the policy's own page.
  if (props.currentPathname === policy.slug) {
    silenceLogger(policy.slug, 'pathname');
    return null;
  }

  const hasSnoozeCookie = Boolean(getCookie(formatSlugForCookie(policy.slug)));
  if (hasSnoozeCookie) {
    silenceLogger(policy.slug, 'snooze is on');
    return null;
  }

  const onAccept = () => {
    const eventParams = {
      policy_slug: policy.slug,
      policy_content: policy.contentId,
    };
    trackEvent(EVENT_POLICY_AGREED, eventParams);
    return policy.onAccept();
  };

  return (
    <PolicyModal
      onAccept={onAccept}
      renderMarkdown={props.renderMarkdown}
      title={policy.title}
      tldr={policy.tldr}
      url={`https://zapier.com/${policy.slug}`}
    />
  );
};
